import { lazy, Suspense } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import { AuthGuard } from '@/app/router/guards/AuthGuard';
import { PermissionGuard } from '@/app/router/guards/PermissionGuard';
import { ClientBlockedGuard } from '@/app/router/guards/ClientBlockedGuard';
import { WalletBlockedGuard } from '@/app/router/guards/WalletBlockedGuard';
import { RouteSpinner } from '@/app/router/RouteSpinner';
import { AppLayout } from '@/layouts/AppLayout';
import { DashboardPage } from '@/features/dashboard/pages/DashboardPage';
import { BillboardDetailPage } from '@/features/billboards/pages/BillboardDetailPage';
import { BillboardsPage } from '@/features/billboards/pages/BillboardsPage';
import { NearbyBillboardsPage } from '@/features/billboards/pages/NearbyBillboardsPage';
import { CampaignCreatePage } from '@/features/campaigns/pages/CampaignCreatePage';
import { CampaignDetailPage } from '@/features/campaigns/pages/CampaignDetailPage';
import { CampaignRelaunchPage } from '@/features/campaigns/pages/CampaignRelaunchPage';
import { CampaignsPage } from '@/features/campaigns/pages/CampaignsPage';
import { GalleryPage } from '@/features/gallery/pages/GalleryPage';
import { CouponPage } from '@/features/coupon/pages/CouponPage';
import { ProfilePage } from '@/features/profile/pages/ProfilePage';
import { NotificationPage } from '@/features/notification/pages/NotificationPage';
import { PoliciesPage } from '@/features/policies/pages/PoliciesPage';
import WalletPage from '@/features/wallet/pages/WalletPage';
import ClientsPage from '@/features/clients/pages/ClientsPage';
import AddClientPage from '@/features/clients/pages/AddClientPage';
import EditClientPage from '@/features/clients/pages/EditClientPage';
import ClientDetailPage from '@/features/clients/pages/ClientDetailPage';
import {
  TeamPage,
  AddTeamMemberPage,
  EditTeamMemberPage,
  TeamMemberDetailPage,
} from '@/features/team';
import { WishlistPage } from '@/features/wishlist/pages/WishlistPage';
import { ReportsPage } from '@/features/reports/pages/ReportsPage';
import { ReportDetailPage } from '@/features/reports/pages/ReportDetailPage';
import { TotalAnalyticsPage } from '@/features/totalAnalytics/pages/TotalAnalyticsPage';
import { routes } from '@/shared/config/routes';

const TutorialPage = lazy(() =>
  import('@/features/tutorial/pages/TutorialPage').then((module) => ({
    default: module.TutorialPage,
  })),
);

const SupportLayout = lazy(() => import('@/features/support/pages/SupportLayout'));
const SupportPage = lazy(() => import('@/features/support/pages/SupportPage'));
const SupportChatPage = lazy(
  () => import('@/features/support/pages/SupportChatPage'),
);

export const appShellRoutes: RouteObject[] = [
  {
    element: <AuthGuard />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: routes.dashboard, element: <DashboardPage /> },
          { path: routes.screens, element: <BillboardsPage embedded /> },
          {
            path: '/screens/:id',
            element: <BillboardDetailPage embedded />,
          },
          {
            path: routes.nearbyScreens,
            element: <NearbyBillboardsPage />,
          },
          {
            path: routes.nearbyScreensLegacy,
            element: <Navigate to={routes.nearbyScreens} replace />,
          },

          { path: '/nearby', element: <Navigate to={routes.nearbyScreens} replace /> },
          
          { path: routes.campaigns, element: <CampaignsPage /> },
          {
            element: <ClientBlockedGuard redirectTo={routes.campaigns} />,
            children: [
              { path: routes.campaignCreate, element: <CampaignCreatePage /> },
              {
                path: '/campaigns/create/:mode',
                element: <CampaignCreatePage />,
              },
              {
                path: '/campaigns/:id/relaunch',
                element: <CampaignRelaunchPage />,
              },
            ],
          },
          { path: '/campaigns/:id', element: <CampaignDetailPage /> },
          { path: routes.clients, element: <ClientsPage /> },
          { path: '/clients/add', element: <AddClientPage /> },
          { path: '/clients/:id/edit', element: <EditClientPage /> },
          { path: '/clients/:id', element: <ClientDetailPage /> },
          { path: routes.team, element: <TeamPage /> },
          { path: '/team/add', element: <AddTeamMemberPage /> },
          { path: '/team/:id/edit', element: <EditTeamMemberPage /> },
          { path: '/team/:id', element: <TeamMemberDetailPage /> },
          {
            element: <PermissionGuard required="gallery_module_access" />,
            children: [{ path: routes.gallery, element: <GalleryPage /> }],
          },
          { path: routes.reports, element: <ReportsPage /> },
          { path: '/report-detail/:id', element: <ReportDetailPage /> },
          { path: routes.coupon, element: <CouponPage /> },
          { path: routes.wishlist, element: <WishlistPage /> },
          {
            element: <WalletBlockedGuard redirectTo={routes.campaigns} />,
            children: [
              {
                element: <PermissionGuard required="wallet_module_access" />,
                children: [{ path: routes.wallet, element: <WalletPage /> }],
              },
            ],
          },
          { path: routes.totalAnalytics, element: <TotalAnalyticsPage /> },
          { path: routes.profile, element: <ProfilePage /> },
          { path: routes.notifications, element: <NotificationPage /> },
          {
            path: routes.appTutorial,
            element: (
              <Suspense fallback={<RouteSpinner />}>
                <TutorialPage embedded />
              </Suspense>
            ),
          },
          { path: routes.policies, element: <PoliciesPage /> },
          {
            element: (
              <Suspense fallback={<RouteSpinner />}>
                <SupportLayout />
              </Suspense>
            ),
            children: [
              { path: routes.support, element: <SupportPage /> },
              { path: '/support/chat/:id', element: <SupportChatPage /> },
            ],
          },
        ],
      },
    ],
  },
];
